/**
 * format.ts — Formatting utilities for the Kapi POS web app.
 *
 * Money is stored as a plain number (MXN, see shared/types/common.ts).
 * All formatters are pure functions — no side effects.
 *
 * C# equivalent: HelpFunctions.applyMoneyFormat / Helper.moneyFormat
 * in pos_app_sell.js and related legacy files.
 */

import type { Money } from '@netcom/shared';

// ---------------------------------------------------------------------------
// Money
// ---------------------------------------------------------------------------

/** MXN formatter — produces "$1,234.56" */
const MXN_FORMATTER = new Intl.NumberFormat('es-MX', {
  style: 'currency',
  currency: 'MXN',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

/**
 * Format a Money value as a localized MXN currency string.
 *
 * @example
 * formatMoney(1234.56) // "$1,234.56"
 * formatMoney(0)       // "$0.00"
 */
export function formatMoney(value: Money): string {
  return MXN_FORMATTER.format(value);
}

/**
 * Format a Money value as a compact number without currency symbol.
 * Useful for tight UI spaces.
 *
 * @example
 * formatMoneyPlain(1234.56) // "1,234.56"
 */
export function formatMoneyPlain(value: Money): string {
  return value.toLocaleString('es-MX', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

// ---------------------------------------------------------------------------
// Dates
// ---------------------------------------------------------------------------

/**
 * Format an ISO date string as a short local date.
 *
 * @example
 * formatDate("2026-03-24T21:30:00Z") // "24/03/2026"
 */
export function formatDate(iso: string): string {
  return formatearFechaApi(iso);
}

/**
 * Format an ISO date string as a short local time.
 *
 * @example
 * formatTime("2026-03-24T21:30:00Z") // "09:30 p.m."
 */
export function formatTime(iso: string): string {
  return formatearFechaApi(iso, { hour: '2-digit', minute: '2-digit', hour12: true });
}

// ---------------------------------------------------------------------------
// Numbers
// ---------------------------------------------------------------------------

/**
 * Format a decimal percentage for display.
 *
 * @example
 * formatPercent(0.16) // "16%"
 * formatPercent(0.075) // "7.5%"
 */
export function formatPercent(rate: number): string {
  return `${(rate * 100).toLocaleString('es-MX', { maximumFractionDigits: 2 })}%`;
}

/** DATETIME/DATE del API son componentes locales de prod, incluso al recibir un Z heredado. */
export function fechaApiDate(value: string): Date {
  const match = /^(\d{4}-\d{2}-\d{2})(?:[T ](\d{2}:\d{2})(?::(\d{2}))?)?/.exec(value);
  return match ? new Date(`${match[1]}T${match[2] ?? '00:00'}:${match[3] ?? '00'}Z`) : new Date(NaN);
}

export function formatearFechaApi(
  value: string | null | undefined,
  options: Intl.DateTimeFormatOptions = { day: '2-digit', month: '2-digit', year: 'numeric' },
  locale = 'es-MX',
): string {
  if (!value) return '—';
  const date = fechaApiDate(value);
  if (!Number.isFinite(date.getTime())) return value;
  return new Intl.DateTimeFormat(locale, { ...options, timeZone: 'UTC' }).format(date);
}

/** Reloj inicial de formularios; los registros definitivos se fechan en el API. */
export function hoyProduccion(ahora = new Date(), difHours = 0): string {
  return new Intl.DateTimeFormat('en-CA', { timeZone: 'America/Hermosillo', year: 'numeric', month: '2-digit', day: '2-digit' })
    .format(new Date(ahora.getTime() + difHours * 3_600_000));
}

/** Fecha `YYYY-MM-DD` de un valor del API que puede venir como el JsonResult de MVC5 (`/Date(ms)/`, p. ej. el detalle
 * del cliente, customers.fechas.ts) o en ISO; la de MVC se lee en la zona de prod. */
export function fechaDeApi(value: string | null | undefined): string {
  const mvc = /^\/Date\((-?\d+)\)\/$/.exec(value ?? '');
  return mvc ? hoyProduccion(new Date(Number(mvc[1]))) : (value ?? '').slice(0, 10);
}

export function ahoraProduccion(ahora = new Date(), difHours = 0): string {
  const parts = Object.fromEntries(new Intl.DateTimeFormat('en-CA', {
    timeZone: 'America/Hermosillo', year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', second: '2-digit', hourCycle: 'h23',
  }).formatToParts(new Date(ahora.getTime() + difHours * 3_600_000)).map(p => [p.type, p.value]));
  return `${parts['year']}-${parts['month']}-${parts['day']} ${parts['hour']}:${parts['minute']}:${parts['second']}`;
}
